import { useState } from "react";
import { useBackupStore } from "../store/backupStore";
import { checkToken, ensureRepo } from "../lib/backup-sync";
import { pushNow } from "../lib/auto-push";
import { restoreFromBackup } from "../lib/restore";

// BackupModal — setup + status for the git-canonical durability path.
// Distinct from the per-channel Sync feature (that publishes ONE
// channel JSON to a chosen path for team sharing). This backs up
// EVERYTHING (channels + library + templates + prefs) to one personal
// repo so a browser cache wipe is recoverable.

interface Props {
  onClose: () => void;
}

export function BackupModal({ onClose }: Props) {
  const { token, repo, autoPush, lastPushedOid, lastPushedAt,
          setToken, setRepo, setAutoPush, clear } = useBackupStore();
  const [tokenDraft, setTokenDraft] = useState(token ?? "");
  const [repoDraft, setRepoDraft] = useState(repo ?? "");
  const [busy, setBusy] = useState<"checking" | "saving" | "pushing" | "restoring" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  async function save() {
    setErr(null);
    setMsg(null);
    const t = tokenDraft.trim();
    const r = repoDraft.trim();
    if (!t || !r) {
      setErr("Need both a PAT and a repo (owner/name).");
      return;
    }
    if (!/^[^/\s]+\/[^/\s]+$/.test(r)) {
      setErr(`Repo "${r}" should be "<owner>/<name>" — no slashes inside the name.`);
      return;
    }
    setBusy("checking");
    try {
      const login = await checkToken(t);
      setMsg(`✓ token valid, authenticated as ${login}`);
      setBusy("saving");
      await ensureRepo({ token: t, repo: r });
      setToken(t);
      setRepo(r);
      setMsg(`✓ saved — backups go to ${r}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handlePushNow() {
    setErr(null);
    setMsg(null);
    setBusy("pushing");
    try {
      const result = await pushNow();
      setMsg(`✓ pushed ${result.fileCount} file${result.fileCount === 1 ? "" : "s"} → ${result.oid.slice(0, 7)}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleRestore() {
    if (!token || !repo) return;
    if (restoreConfirm !== "RESTORE") {
      setErr(`Type "RESTORE" (uppercase) in the confirm box to enable.`);
      return;
    }
    setErr(null);
    setMsg(null);
    setBusy("restoring");
    try {
      const report = await restoreFromBackup({ token, repo });
      const skipNote = report.skipped.length > 0
        ? ` (${report.skipped.length} file${report.skipped.length === 1 ? "" : "s"} skipped — see console)`
        : "";
      if (report.skipped.length > 0) {
        console.warn("[restore] skipped files:", report.skipped);
      }
      setMsg(
        `✓ restored ${report.channels} channel${report.channels === 1 ? "" : "s"}, ` +
        `${report.library} library doc${report.library === 1 ? "" : "s"}, ` +
        `${report.templates} template${report.templates === 1 ? "" : "s"}` +
        `${report.defaultTemplate ? " + default-template pref" : ""}${skipNote}. ` +
        `Reloading in 2s…`,
      );
      // Brief delay so the user sees the success line before reload.
      setTimeout(() => { window.location.reload(); }, 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  function handleClear() {
    if (!confirm("Disconnect backup? Token + repo + push history will be cleared from this browser. The remote repo itself is untouched.")) return;
    clear();
    setTokenDraft("");
    setRepoDraft("");
    setMsg("Backup disconnected from this browser.");
  }

  const configured = !!token && !!repo;
  const status = !configured ? "off" : !lastPushedAt ? "ready" : autoPush ? "auto" : "manual";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Playground backup</h2>
        <div className="subtitle">
          One personal GitHub repo holding the whole Playground — channels,
          library, templates, prefs — versioned by every change. Different
          from per-channel <strong>Sync</strong> (which shares one channel
          with a team). Use both, neither, or just one.
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label>Fine-grained PAT <span style={{ color: "var(--fg-dimmer)" }}>(contents: read+write on the target repo)</span></label>
          <input
            type="password"
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            placeholder="github_pat_…"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Repo</label>
          <input
            type="text"
            value={repoDraft}
            onChange={(e) => setRepoDraft(e.target.value)}
            placeholder="username/playground-backup"
          />
          <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 4 }}>
            Auto-created as a private repo if it doesn't exist (PAT needs repo-creation permission).
          </div>
        </div>

        <div className="field">
          <label className="checkbox-row" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={autoPush}
              onChange={(e) => setAutoPush(e.target.checked)}
              disabled={!configured}
            />
            <span>
              Auto-push on every change <span style={{ color: "var(--fg-dimmer)" }}>(30s debounce, 3 min ceiling)</span>
            </span>
          </label>
        </div>

        {err && <div className="banner banner-error" style={{ margin: "8px 0" }}>{err}</div>}
        {msg && <div style={{ fontSize: 12, color: "var(--fg-dim)", padding: "6px 0" }}>{msg}</div>}

        <div className="field" style={{ marginTop: 12 }}>
          <label>Status</label>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--fg)", padding: "4px 0" }}>
            <BackupStatusLine status={status} lastPushedAt={lastPushedAt} lastPushedOid={lastPushedOid} repo={repo} />
          </div>
        </div>

        <div className="actions">
          {configured && (
            <button className="btn btn-ghost" onClick={handleClear} disabled={busy !== null}>
              Disconnect
            </button>
          )}
          <span style={{ flex: 1 }} />
          {configured && (
            <button className="btn btn-ghost" onClick={handlePushNow} disabled={busy !== null}>
              {busy === "pushing" ? "Pushing…" : "Push now"}
            </button>
          )}
          <button className="btn btn-primary" onClick={save} disabled={busy !== null || (!tokenDraft.trim() && !repoDraft.trim())}>
            {busy === "checking" ? "Checking…" : busy === "saving" ? "Saving…" : "Save"}
          </button>
        </div>

        {configured && (
          <div className="restore-section">
            {!restoring ? (
              <button
                type="button"
                className="link-btn restore-toggle"
                onClick={() => { setRestoring(true); setRestoreConfirm(""); setErr(null); setMsg(null); }}
              >
                Restore from backup →
              </button>
            ) : (
              <>
                <div className="restore-head">Restore from {repo}</div>
                <div className="restore-warn">
                  Destructive. <strong>Replaces all local channels, library docs, and templates</strong> with whatever's in the backup repo. Local changes since the last push will be lost. The remote repo itself is untouched.
                </div>
                <div className="restore-confirm-row">
                  <label>
                    Type <code>RESTORE</code> to confirm:
                  </label>
                  <input
                    type="text"
                    value={restoreConfirm}
                    onChange={(e) => setRestoreConfirm(e.target.value)}
                    placeholder="RESTORE"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <div className="actions" style={{ marginTop: 8 }}>
                  <button className="btn btn-ghost" onClick={() => { setRestoring(false); setRestoreConfirm(""); }} disabled={busy === "restoring"}>
                    Cancel
                  </button>
                  <span style={{ flex: 1 }} />
                  <button
                    className="btn btn-primary"
                    style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                    onClick={handleRestore}
                    disabled={busy !== null || restoreConfirm !== "RESTORE"}
                  >
                    {busy === "restoring" ? "Restoring…" : "Restore + reload"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BackupStatusLine({
  status, lastPushedAt, lastPushedOid, repo,
}: {
  status: "off" | "ready" | "manual" | "auto";
  lastPushedAt: number | null;
  lastPushedOid: string | null;
  repo: string | null;
}) {
  if (status === "off") return <span style={{ color: "var(--fg-dim)" }}>not configured</span>;
  if (status === "ready") return <span style={{ color: "var(--accent)" }}>configured · no push yet ({repo})</span>;
  const when = lastPushedAt ? formatRelative(lastPushedAt) : "?";
  const oid = lastPushedOid ? lastPushedOid.slice(0, 7) : "?";
  const tag = status === "auto" ? "auto" : "manual";
  return (
    <span>
      <span style={{ color: "var(--accent)" }}>● </span>
      last push {when} · {oid} · {tag}
    </span>
  );
}

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.round(diff / 3600_000)}h ago`;
  return `${Math.round(diff / 86400_000)}d ago`;
}
